import { Service, computed, signal } from '@angular/core';
import {
  type SignInOutput,
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { PASSWORD_SIGN_IN_ENABLED, SSO_PROVIDER, SSO_SIGN_IN_ENABLED } from '../features';
import { clearSessionKeys } from '../vault/session-key-store';

export type AuthStep =
  | { kind: 'signIn' }
  | { kind: 'newPassword' }
  | { kind: 'totpSetup'; setupUri: string; secret: string }
  | { kind: 'totpCode' };

const APP_NAME = 'Notaryx';

/** Set while the first SSO sign-in of a user is retried once (see backend/sso-link.ts). */
const SSO_RETRY_KEY = 'notaryx.sso-retry';

@Service()
export class AuthService {
  private readonly _email = signal<string | null>(null);
  private readonly _userId = signal<string | null>(null);
  private readonly _step = signal<AuthStep>({ kind: 'signIn' });
  private readonly _pendingEmail = signal('');
  private readonly _ssoError = signal<string | null>(null);

  readonly email = this._email.asReadonly();
  /** The Cognito `sub` of the signed-in user. */
  readonly userId = this._userId.asReadonly();
  readonly step = this._step.asReadonly();
  /** The email typed at sign-in, while the sign-in is still in progress. */
  readonly pendingEmail = this._pendingEmail.asReadonly();
  readonly isAuthenticated = computed(() => this._userId() !== null);
  /** Why the last SSO sign-in failed, as reported by the hosted UI on its way back. */
  readonly ssoError = this._ssoError.asReadonly();
  /**
   * The password just typed at sign-in or chosen, kept in memory only until the file vault
   * derives its key from it (see VaultService). Never stored.
   */
  private typedPassword: string | null = null;

  /** Hands over the password typed in this tab, once. */
  takePassword(): string | null {
    const password = this.typedPassword;
    this.typedPassword = null;
    return password;
  }

  /** A fresh access token for the API, refreshed by Amplify when it is about to expire. */
  async accessToken(): Promise<string | null> {
    try {
      const { tokens } = await fetchAuthSession();
      return tokens?.accessToken.toString() ?? null;
    } catch {
      return null;
    }
  }

  /** Sends the browser to the company identity provider; it comes back to /login. */
  async signInWithSso(): Promise<void> {
    if (!SSO_SIGN_IN_ENABLED) throw new Error('SSO sign-in is not enabled.');
    this._ssoError.set(null);
    try {
      await signInWithRedirect({ provider: { custom: SSO_PROVIDER } });
    } catch (error) {
      throw new Error(toMessage(error), { cause: error });
    }
  }

  /**
   * Restores an existing session (called once at startup). On the way back from the hosted UI,
   * Amplify first exchanges the code for tokens: getCurrentUser() waits for it.
   */
  async restoreSession(): Promise<void> {
    this.handleSsoFailure();
    try {
      const { userId } = await getCurrentUser();
      const { email } = await fetchUserAttributes();
      this._email.set(email ?? null);
      this._userId.set(userId);
      globalThis.sessionStorage?.removeItem(SSO_RETRY_KEY);
    } catch {
      this._email.set(null);
      this._userId.set(null);
      // No session (signed out, or it expired while the browser was closed): no file key may
      // outlive it.
      await clearSessionKeys();
    }
  }

  /**
   * Reads the error the hosted UI came back with, if any. The first SSO sign-in of a user always
   * fails once, right after their SSO identity was linked to their account: it is started again.
   * Must run in the same task as configureAuth(), before Amplify reports the failure.
   */
  private handleSsoFailure(): void {
    const params = new URLSearchParams(globalThis.location?.search ?? '');
    const description = params.get('error_description') ?? params.get('error');
    if (!description) return;
    globalThis.history?.replaceState(null, '', globalThis.location.pathname);
    const retried = globalThis.sessionStorage?.getItem(SSO_RETRY_KEY);
    if (/already found an entry for username/i.test(description) && !retried) {
      globalThis.sessionStorage?.setItem(SSO_RETRY_KEY, '1');
      // Only once Amplify has cleared the failed attempt, or it would clear the new one too.
      const stop = Hub.listen('auth', ({ payload }) => {
        if (payload.event !== 'signInWithRedirect_failure') return;
        stop();
        this.signInWithSso().catch((err: unknown) =>
          this._ssoError.set(err instanceof Error ? err.message : String(err)),
        );
      });
      return;
    }
    globalThis.sessionStorage?.removeItem(SSO_RETRY_KEY);
    // Errors thrown by the pre sign-up trigger come back prefixed.
    const message = description.replace(/^PreSignUp failed with error\s*/i, '').trim();
    this._ssoError.set(message || 'Sign-in through SSO failed. Please try again.');
  }

  /** Email + password sign-in (SRP), when PASSWORD_SIGN_IN_ENABLED (core/features.ts). */
  async signIn(email: string, password: string): Promise<void> {
    if (!PASSWORD_SIGN_IN_ENABLED) throw new Error('Password sign-in is not enabled.');
    this._pendingEmail.set(email);
    this.typedPassword = password;
    try {
      await this.run(() => signIn({ username: email, password }));
    } catch (err) {
      this.typedPassword = null;
      throw err;
    }
  }

  async confirmNewPassword(newPassword: string): Promise<void> {
    await this.run(() => confirmSignIn({ challengeResponse: newPassword }));
    // The account password also encrypts the files: from now on it is this one.
    this.typedPassword = newPassword;
  }

  async confirmTotp(code: string): Promise<void> {
    await this.run(() => confirmSignIn({ challengeResponse: code }));
  }

  /** Abandons an in-progress sign-in and returns to the first step. */
  restart(): void {
    this.typedPassword = null;
    this._pendingEmail.set('');
    this._step.set({ kind: 'signIn' });
  }

  /**
   * Emails a reset code. Resolves the same way whether or not the address has an account
   * (the client has preventUserExistenceErrors enabled).
   */
  async sendPasswordReset(email: string): Promise<void> {
    try {
      await resetPassword({ username: email });
    } catch (error) {
      throw new Error(toMessage(error), { cause: error });
    }
  }

  async confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
    } catch (error) {
      const name = error instanceof Error ? error.name : '';
      const message = /CodeMismatch|ExpiredCode/.test(name)
        ? 'That code is not valid or has expired. Ask for a new one.'
        : toMessage(error);
      throw new Error(message, { cause: error });
    }
  }

  /** Ends every session of this user, on all devices. */
  async signOut(): Promise<void> {
    await signOut({ global: true }).catch(() => signOut());
    await this.forget();
  }

  /** Forgets the session in this browser only, e.g. after the API rejected it. */
  async dropSession(): Promise<void> {
    await signOut().catch(() => undefined);
    await this.forget();
  }

  private async forget() {
    this.typedPassword = null;
    this._email.set(null);
    this._userId.set(null);
    this.restart();
    await clearSessionKeys();
  }

  private async run(action: () => Promise<SignInOutput>): Promise<void> {
    let output: SignInOutput;
    try {
      output = await action();
    } catch (error) {
      throw new Error(toMessage(error), { cause: error });
    }
    await this.handle(output);
  }

  private async handle({ nextStep }: SignInOutput): Promise<void> {
    switch (nextStep.signInStep) {
      case 'DONE':
        await this.restoreSession();
        this._step.set({ kind: 'signIn' });
        return;
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        this._step.set({ kind: 'newPassword' });
        return;
      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
        const { totpSetupDetails } = nextStep;
        this._step.set({
          kind: 'totpSetup',
          secret: totpSetupDetails.sharedSecret,
          setupUri: totpSetupDetails.getSetupUri(APP_NAME, this._pendingEmail()).toString(),
        });
        return;
      }
      case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION':
        // Only TOTP is enabled on the pool; pick it directly.
        return this.run(() => confirmSignIn({ challengeResponse: 'TOTP' }));
      case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
        return this.run(() => confirmSignIn({ challengeResponse: 'TOTP' }));
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        this._step.set({ kind: 'totpCode' });
        return;
      case 'RESET_PASSWORD':
        this.restart();
        throw new Error('Your password must be reset. Please contact an administrator.');
      default:
        this.restart();
        throw new Error('Unsupported sign-in step. Please contact an administrator.');
    }
  }
}

function toMessage(error: unknown): string {
  const name = error instanceof Error ? error.name : '';
  const message = error instanceof Error ? error.message : '';
  switch (name) {
    case 'NotAuthorizedException':
      if (/temporary password has expired/i.test(message)) {
        return 'Your invitation has expired. Please ask an administrator to send a new one.';
      }
      if (/session/i.test(message)) {
        return 'Your session has expired. Please sign in again.';
      }
      return 'Incorrect email or password.';
    case 'UserNotFoundException':
      return 'Incorrect email or password.';
    case 'CodeMismatchException':
    case 'EnableSoftwareTokenMFAException':
      return 'That code is not valid. Check the time on your device and try the next code.';
    case 'ExpiredCodeException':
      return 'This code has expired. Wait for a new code and try again.';
    case 'InvalidPasswordException':
      return message || 'This password does not meet the requirements.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'PasswordResetRequiredException':
      return 'Your password must be reset. Please contact an administrator.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
